import type { AccessPointEdgeEventLogEvent } from './access-point-edge-hub.service';

/**
 * Evento `aem:event` do hub: o registro do stream do AEM como está no Redis.
 * `type`, `allowed` e `lvl` chegam como texto (`"1"`), e o `payload` pode vir
 * como objeto ou como JSON em texto. Tudo opcional — o contrato do tempo real
 * não está fechado, e o front só usa o que vier.
 */
export interface AemEventPayload {
  id?: string;
  type?: string | number;
  allowed?: string | number | boolean;
  code?: string;
  msg?: string;
  message?: string;
  lvl?: string | number;
  level?: string | number;
  ts?: string;
  edgeId?: string;
  tenantId?: string;
  personId?: string | null;
  correlationId?: string | null;
  payload?: Record<string, unknown> | string | null;
}

/** `true`/`false`, `"1"`/`"0"`, `"true"`/`"false"`, `1`/`0` → boolean; o resto → `undefined`. */
export function toEdgeBoolean(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === '1' || normalized === 'true') return true;
    if (normalized === '0' || normalized === 'false') return false;
  }
  return undefined;
}

/** Número em número ou em texto (`"2"`); o resto → `undefined`. */
export function toEdgeNumber(value: unknown): number | undefined {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

const parsePayload = (
  payload: AemEventPayload['payload']
): Record<string, unknown> | null => {
  if (payload && typeof payload === 'object') return payload;
  if (typeof payload === 'string' && payload.trim()) {
    try {
      const parsed: unknown = JSON.parse(payload);
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
    } catch {
      return null;
    }
  }
  return null;
};

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value : undefined;

/**
 * Converte um `aem:event` no evento que o monitor já consome pelo `EdgeEventLog`.
 *
 * Só passa acesso decidido no equipamento (`code` com `_ON_EDGE`, ou
 * `DecisionSource = Edge` no payload): as decisões online já chegam pelo
 * `EdgeEventLog`, e repassá-las daqui duplicaria as linhas. Devolve `null` para
 * o resto, e quando falta o `edgeId`.
 *
 * `ts` já deve vir convertido para o fuso da conta (`toLocalPayload` do hub);
 * `fallbackOccurredOn` só entra quando ele falta.
 */
export function mapAemEventToEdgeEventLog(
  evt: AemEventPayload | null | undefined,
  fallbackOccurredOn: string
): AccessPointEdgeEventLogEvent | null {
  const edgeId = asString(evt?.edgeId);
  if (!evt || !edgeId) return null;

  const payload = parsePayload(evt.payload);
  const code = asString(evt.code) ?? '';
  const isEdgeDecision =
    code.toUpperCase().includes('_ON_EDGE') || payload?.['DecisionSource'] === 'Edge';
  if (!isEdgeDecision) return null;

  // Sem `allowed` em lado nenhum, o próprio código diz: `DENY_ON_EDGE` ou `ALLOWED_ON_EDGE*`.
  const allowed =
    toEdgeBoolean(evt.allowed) ??
    toEdgeBoolean(payload?.['Allowed']) ??
    !code.toUpperCase().startsWith('DENY');

  return {
    edgeId,
    tenantId: asString(evt.tenantId) ?? '',
    type: toEdgeNumber(evt.type) ?? 0,
    level: toEdgeNumber(evt.lvl ?? evt.level) ?? 0,
    occurredOnUtc: asString(evt.ts) ?? fallbackOccurredOn,
    personId: asString(evt.personId) ?? asString(payload?.['PersonId']) ?? null,
    allowed,
    code,
    message: asString(evt.msg) ?? asString(evt.message) ?? code,
    // O `EventId` da placa é o mesmo `AccessLogId` do histórico: serve de chave
    // para o monitor não inserir duas vezes o acesso que vier pelos dois canais.
    correlationId:
      asString(evt.correlationId) ?? asString(payload?.['EventId']) ?? null,
    payloadJson: payload ? JSON.stringify({ ...payload, Allowed: allowed }) : '',
  };
}
